/**
 * Copyright (C) 2026 Guido Berhoerster <guido+voxit@berhoerster.name>
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal
 * in the Software without restriction, including without limitation the rights
 * to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
 * copies of the Software, and to permit persons to whom the Software is
 * furnished to do so, subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in all
 * copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
 * AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
 * OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
 * SOFTWARE.
 */

export class BaseError extends Error {
    constructor(message, options = {}) {
        super(message, options);
    }
}

export class FetchError extends BaseError {
    constructor(message, fetchError) {
        super(message, {cause: fetchError});
    }
}

export class HTTPError extends BaseError {
    constructor(status, message = "Unknown HTTP error") {
        super(message);
        this.status = status;
    }
}

export class APIError extends HTTPError {
    constructor(status, {type = "about:blank", title: message = "Unknown API error", detail = ""}) {
        super(status, message);
        this.type = type;
        this.detail = detail;
    }
}

export class AuthenticationRequiredError extends APIError {
    constructor(status, {type = "about:blank", title: message = "Authentication required", detail = ""}) {
        super(status, {type, title: message, detail});
    }
}

export class StatementExistsError extends APIError {
    constructor(status, {type = "about:blank", title: message = "Statement already exists", detail = ""}) {
        super(status, {type, title: message, detail});
    }
}

export class InvalidLanguageTagError extends APIError {
    constructor(status, {type = "about:blank", title: message = "Invalid language tag", detail = ""}) {
        super(status, {type, title: message, detail});
    }
}

export const ModerationStatus = Object.freeze({
    Rejected: "rejected",
    None: "none",
    Accepted: "accepted",
});

class PaginationLinks {
    firstURL = null;
    nextURL = null;

    constructor(headerValue, baseURL) {
        // TODO: proper Link header (RFC 8288) parser
        for (const value of headerValue.split(",")) {
            [...value.matchAll(/<([^>]+)>\s*;\s*rel="(first|next)"/g)].map(
                ([, url, rel]) => this[`${rel}PageURL`] = new URL(url, baseURL)
            );
        }
    }
}

export class PaginatedResponse {
    constructor(data, paginationLinks) {
        this.data = data;
        this.paginationLinks = paginationLinks;
    }
}

export class Conversation {
    constructor(
        {
            id = "",
            topic = "",
            description = "",
            is_active: isActive = true,
            statements_allowed: statementsAllowed = true,
            results_enabled: resultsEnabled = true,
            strict_moderation: strictModeration = false,
            created = "",
            last_modified: lastModified = "",
        } = {}
    ) {
        this.id = id;
        this.topic = topic;
        this.description = description;
        this.isActive = isActive;
        this.statementsAllowed = statementsAllowed;
        this.resultsEnabled = resultsEnabled;
        this.strictModeration = strictModeration;
        this.created = new Date(created);
        this.lastModified = new Date(lastModified);
    }

    toJSON() {
        return {
            topic: this.topic,
            description: this.description,
            is_active: this.isActive,
            statements_allowed: this.statementsAllowed,
            results_enabled: this.resultsEnabled,
            strict_moderation: this.strictModeration,
        }
    }
}

export class ConversationTranslation {
    constructor(
        {
            topic = "",
            description = "",
            language = "",
            created = "",
            last_modified: lastModified = "",
        } = {}
    ) {
        this.topic = topic;
        this.description = description;
        this.language = language;
        this.created = new Date(created);
        this.lastModified = new Date(lastModified);
    }

    toJSON() {
        return {
            topic: this.topic,
            description: this.description,
        }
    }
}

export class Statement {
    constructor(
        {
            id = "",
            text = "",
            is_meta: isMeta = false,
            is_seed: isSeed = true,
            moderation_status: moderationStatus = ModerationStatus.None,
            created = "",
            last_modified: lastModified = "",
        } = {}
    ) {
        this.id = id;
        this.text = text;
        this.isMeta = isMeta;
        this.isSeed = isSeed;
        this.moderationStatus = moderationStatus;
        this.created = new Date(created);
        this.lastModified = new Date(lastModified);
    }

    toJSON() {
        return {
            text: this.text,
            is_meta: this.isMeta,
            is_seed: this.isSeed,
            moderation_status: this.moderationStatus,
        }
    }
}

export class StatementsParams {
    static #allowedParams = ["cursor", "limit", "moderation_status", "order", "text"];
    #urlSearchParams;

    constructor(urlSearchParams) {
        urlSearchParams ??= new URLSearchParams();
        this.#urlSearchParams = new URLSearchParams();
        for (const name of StatementsParams.#allowedParams) {
            if (urlSearchParams.has(name)) {
                for (const value of urlSearchParams.getAll(name)) {
                    this.#urlSearchParams.append(name, value);
                }
            }
        }
    }

    get(name) {
        return this.#urlSearchParams.get(name);
    }

    getAll(name) {
        return this.#urlSearchParams.getAll(name);
    }

    toString() {
        return this.#urlSearchParams.toString();
    }
}

export class AdminClient {
    constructor(baseURL) {
        const urlWithPath = (path) => {
            const url = new URL(baseURL);
            url.pathname = `/api/v1unstable${path ? "/" : ""}${path ?? ""}`;
            return url;
        }
        const urlAppendPath = (origURL, path) => {
            const url = new URL(origURL);
            url.pathname = `${url.pathname}/${path}`;
            return url;
        }

        this.baseURL = urlWithPath("")
        this.conversationsURL = urlWithPath("conversations");
        this.conversationURL = conversationID => urlAppendPath(this.conversationsURL, encodeURIComponent(conversationID));
        this.conversationTranslationsURL = conversationID => urlAppendPath(this.conversationURL(conversationID), "translations");
        this.conversationTranslationURL = (conversationID, language) => urlAppendPath(this.conversationTranslationsURL(conversationID), encodeURIComponent(language));
        this.statementsURL = conversationID => urlAppendPath(this.conversationURL(conversationID), "statements");
        this.statementURL = (conversationID, statementID) => urlAppendPath(this.statementsURL(conversationID), encodeURIComponent(statementID));
        this.loginURL = urlWithPath("auth/login");
        this.logoutURL = urlWithPath("auth/logout");
    }

    async handleErrorResponse(response) {
        const contentType = response.headers.get("Content-Type");
        let problemDetails = {type: "", title: "", detail: ""};
        if (contentType?.includes("application/problem+json")) {
            problemDetails = await response.json();
        }

        switch (problemDetails.type) {
        case "tag:voxit.org,2026:api:v1unstable:errors:authentication_required":
            throw new AuthenticationRequiredError(response.status, problemDetails);
        case "tag:voxit.org,2026:api:v1unstable:errors:statement_exists":
            throw new StatementExistsError(response.status, problemDetails);
        case "tag:voxit.org,2026:api:v1unstable:errors:invalid_language_tag":
            throw new InvalidLanguageTagError(response.status, problemDetails);
        default:
            if (response.status > 399) {
                throw new APIError(response.status, problemDetails);
            } else {
                throw new HTTPError(response.status, "unexpected HTTP status");
            }
        }
    }

    async fetchJSON(url, options = {}) {
        let response;
        try {
            response = await fetch(url,
                Object.assign({
                    mode: "cors",
                    credentials: "include",
                }, options)
            );
        } catch(error) {
            throw new FetchError("failed to fetch", {
                cause: error,
            });
        }
        if (!response.ok) {
            await this.handleErrorResponse(response);
        }
        return response.json();
    }

    async fetchJSONWithPagination(url, options = {}) {
        let response;
        try {
            response = await fetch(url,
                Object.assign({
                    mode: "cors",
                    credentials: "include",
                }, options)
            );
        } catch(error) {
            throw new FetchError("failed to fetch", {
                cause: error,
            });
        }
        if (!response.ok) {
            await this.handleErrorResponse(response);
        }
        const data = await response.json();
        const paginationLinks = new PaginationLinks(response.headers.get("link") ?? "", this.baseURL);
        return new PaginatedResponse(data, paginationLinks);
    }

    async submitJSON(method, url, body, options = {}) {
        let response;
        try {
            response = await fetch(url,
                Object.assign({
                    method: method,
                    mode: "cors",
                    credentials: "include",
                    headers: {
                        "Content-Type": "application/json",
                    },
                    body: JSON.stringify(body),
                }, options)
            );
        } catch(error) {
            throw new FetchError("failed to fetch", {
                cause: error,
            });
        }
        if (!response.ok) {
            await this.handleErrorResponse(response);
        }
        return response;
    }

    async logout() {
        const response = await this.submitJSON(
            "POST",
            this.logoutURL,
            {},
        );
        if (response.status !== 200) {
            throw new HTTPError(response.status, `Unexpected HTTP status: ${response.status}`);
        }
        const { oidc_logout: oidcLogoutURL } = await response.json();

        return oidcLogoutURL;
    }

    async fetchConversations() {
        const data = await this.fetchJSON(this.conversationsURL);

        const conversations = [];
        for (const entry of data) {
            const conversation = new Conversation(entry);
            conversations.push(conversation);
        }

        return conversations;
    }

    async createConversation(conversation) {
        const response = await this.submitJSON(
            "POST",
            this.conversationsURL,
            conversation,
        );
        if (response.status !== 201) {
            throw new HTTPError(response.status, `Unexpected HTTP status: ${response.status}`);
        }
    }

    async updateConversation(conversation) {
        const response = await this.submitJSON(
            "PUT",
            this.conversationURL(conversation.id),
            conversation,
        );
        if (response.status !== 204) {
            throw new HTTPError(response.status, `Unexpected HTTP status: ${response.status}`);
        }
    }

    async fetchConversationTranslations(conversationID) {
        const data = await this.fetchJSON(this.conversationTranslationsURL(conversationID));

        const translations = [];
        for (const entry of data) {
            const translation = new ConversationTranslation(entry);
            translations.push(translation)
        }

        return translations;
    }

    async updateConversationTranslation(conversationID, conversationTranslation) {
        const response = await this.submitJSON(
            "PUT",
            this.conversationTranslationURL(conversationID, conversationTranslation.language),
            conversationTranslation,
        );
        if (response.status !== 201 && response.status !== 204) {
            throw new HTTPError(response.status, `Unexpected HTTP status: ${response.status}`);
        }
    }

    async fetchStatements(conversationID, params, paginationURL) {
        params ??= new StatementsParams();
        const url = paginationURL ?? this.statementsURL(conversationID);
        url.search = params;
        const paginatedResponse = await this.fetchJSONWithPagination(url);

        const statements = [];
        for (const entry of paginatedResponse.data) {
            const statement = new Statement(entry);
            statements.push(statement);
        }

        return new PaginatedResponse(statements, paginatedResponse.paginationLinks);
    }

    async addStatement(conversationID, statement) {
        const response = await this.submitJSON(
            "POST",
            this.statementsURL(conversationID),
            statement,
        );
        if (response.status !== 201) {
            throw new HTTPError(response.status, `Unexpected HTTP status: ${response.status}`);
        }
    }

    async updateStatement(conversationID, statement) {
        const response = await this.submitJSON(
            "PUT",
            this.statementURL(conversationID, statement.id),
            statement,
        );
        if (response.status !== 204) {
            throw new HTTPError(response.status, `Unexpected HTTP status: ${response.status}`);
        }
    }
}
